'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { usePageAccess } from '@/context/SiteContext';
import { getPublicContentPreview } from '@/lib/api-client';

/** Plan #144.17 — paths that we KNOW are public without waiting on the
 *  server-side access config. PageGuard's default behavior (full-page
 *  spinner while auth + config resolve) was blocking ~400ms of first
 *  paint on every fresh-visit navigation; users perceived that as a
 *  "full page refresh" even after every child component had been
 *  restructured for independent loading. Short-circuiting these known-
 *  public prefixes lets the page render immediately and each section
 *  show its own skeleton/spinner. If the server-side config later
 *  contradicts (unlikely — these are the public detail pages), the
 *  redirect still fires from the useEffect below. */
const KNOWN_PUBLIC_PREFIXES = [
  '/stocks/',     // stock detail
  '/etf/',        // etf detail
  '/news',        // news list + detail
  '/markets',     // gainers / losers / mood / etc.
  '/heatmap',
  '/ipos',
  '/dividends',
  '/splits',
  '/offerings',
  '/insiders',
  '/congress',
  '/economic-calendar',
  '/earnings',
  '/about',
  '/faq',
  '/pro',
  '/privacy',
  '/terms',
  '/disclaimer',
  '/changelog',
  '/advertise',
  '/developer',
  '/donate',
  '/newsletter',
  '/sectors',
  '/smart-money',
];

function isKnownPublic(pathname: string): boolean {
  if (pathname === '/') return true;
  return KNOWN_PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export default function PageGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isPro, isFull, isAdmin, loading } = useAuth();
  const { getAccess, loaded } = usePageAccess();

  // Track whether content preview is enabled (so we skip redirects)
  const [previewEnabled, setPreviewEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    getPublicContentPreview()
      .then((data) => setPreviewEnabled(data.enabled))
      .catch(() => setPreviewEnabled(false));
  }, []);

  useEffect(() => {
    if (loading || !loaded || previewEnabled === null) return;

    // Admin users always pass
    if (isAdmin) return;

    // Skip guard for auth-related pages
    if (pathname.startsWith('/admin') || pathname.startsWith('/login') || pathname.startsWith('/create-account') || pathname.startsWith('/forgot-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/verify-email')) return;

    const access = getAccess(pathname);

    if (access === 'public') return;

    // When content preview is enabled, don't redirect — the overlay handles it
    if (previewEnabled) return;

    if (access === 'registered') {
      if (!user) {
        router.replace('/login?returnTo=' + encodeURIComponent(pathname));
      }
      return;
    }

    if (access === 'pro') {
      if (!user) {
        router.replace('/login?returnTo=' + encodeURIComponent(pathname));
      } else if (!isPro && !isFull) {
        router.replace('/pro');
      }
      return;
    }

    if (access === 'full') {
      if (!user) {
        router.replace('/login?returnTo=' + encodeURIComponent(pathname));
      } else if (!isFull) {
        router.replace('/pro');
      }
      return;
    }

    if (access === 'admin') {
      if (!user) {
        router.replace('/login?returnTo=' + encodeURIComponent(pathname));
      } else if (!isAdmin) {
        router.replace('/');
      }
      return;
    }
  }, [pathname, loading, loaded, user, isPro, isFull, isAdmin, getAccess, router, previewEnabled]);

  // Plan #144.17 — fast path: known-public pages (stock/ETF detail,
  // markets, news, etc.) render immediately without waiting on auth
  // or access config. Their content is accessible to anons anyway, so
  // there's no redirect risk, and each page's own components own
  // their loading state. This kills the "full page refresh" feel on
  // fresh visits — chart spinner + header skeleton appear on first
  // paint instead of behind a ~400ms centered spinner.
  if (isKnownPublic(pathname)) {
    return <>{children}</>;
  }

  // While loading auth, page access, or preview settings — show the spinner.
  // Previously this returned `children` with the intent of avoiding a spinner
  // flash for logged-in users on public pages, but the side effect was far
  // worse: an anon user clicking a restricted page would briefly see the full
  // page content before the useEffect redirect fired. Spinner-to-content is
  // a much gentler transition than content-to-redirect.
  if (loading || !loaded || previewEnabled === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Admin always passes
  if (isAdmin) {
    return <>{children}</>;
  }

  // Skip guard for auth-related pages
  if (pathname.startsWith('/admin') || pathname.startsWith('/login') || pathname.startsWith('/create-account') || pathname.startsWith('/forgot-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/verify-email')) {
    return <>{children}</>;
  }

  const access = getAccess(pathname);

  if (access === 'public') return <>{children}</>;

  // When content preview is enabled, always render children (overlay handles restriction)
  if (previewEnabled) return <>{children}</>;

  if (access === 'registered' && user) return <>{children}</>;
  if (access === 'pro' && (isPro || isFull)) return <>{children}</>;
  if (access === 'full' && isFull) return <>{children}</>;
  if (access === 'admin' && isAdmin) return <>{children}</>;

  // If we get here, the useEffect redirect hasn't fired yet — show loading
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
